import { useAppStore } from '@/store/useAppStore';
import { isHonorsEligible } from '@gpa-scales/shared-logic/engine/honorsEligibility';
import { GradeLevel, CourseType, AGSubject } from '@gpa-scales/shared-types';
import type { Course, AppSettings } from '@gpa-scales/shared-types';
import { NumericDisplay } from 'calc-controls';
import { HelpTip } from './Tooltip';

const HONORS_CAP_HELP =
  'UC awards at most 8 honors bonus points total across 10th and 11th grade A-G courses, with no more than 4 from 10th grade. Highest-graded honors-eligible courses receive the points first.';

const COURSE_TYPE_LABELS: Record<string, string> = {
  [CourseType.AP]: 'AP',
  [CourseType.IB_HL]: 'IB HL',
  [CourseType.IB_SL]: 'IB SL',
  [CourseType.Honors]: 'Honors',
  [CourseType.DualEnrollment]: 'DE',
  [CourseType.Standard]: 'Standard',
  [CourseType.College]: 'College',
};

function getHonorsCourses(courses: Course[], settings: AppSettings): Course[] {
  // Filter to UC-eligible (A-G, 10th/11th) and honors-eligible
  const ucEligible = courses.filter(
    (c) =>
      c.agSubject !== AGSubject.None &&
      (c.gradeLevel === GradeLevel.Sophomore || c.gradeLevel === GradeLevel.Junior)
  );
  return ucEligible.filter((c) => isHonorsEligible(c, settings));
}

export function HonorsCapCounter() {
  const result = useAppStore((s) => s.result);
  const courses = useAppStore((s) => s.courses);
  const settings = useAppStore((s) => s.settings);
  const schoolContext = useAppStore((s) => s.schoolContext);

  if (!result) {
    return (
      // Bolder: demoted chrome — surface tint, no border, no shadow.
      // Lets the hero GPA card keep visual primacy on the row.
      <div className="rounded-lg bg-surface-secondary p-4">
        <h3 className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-text-muted)]">
          UC Honors Cap
        </h3>
        <p className="text-center text-sm text-[var(--color-text-muted)]">
          Add honors/AP/IB courses to track your UC honors point cap.
        </p>
      </div>
    );
  }

  const honorsCap = result.honorsCap;

  const honorsCourses = getHonorsCourses(courses, settings);
  const tenthGradeHonors = honorsCourses.filter((c) => c.gradeLevel === GradeLevel.Sophomore);
  const eleventhGradeHonors = honorsCourses.filter((c) => c.gradeLevel === GradeLevel.Junior);

  const totalPct = (honorsCap.usedPoints / honorsCap.totalCap) * 100;
  const tenthPct = (honorsCap.tenthGradeUsed / honorsCap.tenthGradeCap) * 100;
  const barColor = honorsCap.usedPoints >= 8 ? 'bg-error' : honorsCap.usedPoints >= 5 ? 'bg-warning' : 'bg-success';

  const ucCapped = result.variants.find((v) => v.label === 'UC Capped Weighted GPA');
  const ucUncapped = result.variants.find((v) => v.label === 'UC Uncapped Weighted GPA');
  const capPenalty =
    ucCapped && ucUncapped ? Math.max(0, ucUncapped.value - ucCapped.value) : 0;
  const capSaturated = honorsCap.usedPoints >= honorsCap.totalCap;
  const showAdvisory = capSaturated && ucCapped !== undefined && ucUncapped !== undefined;

  return (
    // Bolder: tertiary utility — surface tint, no border, no shadow.
    <div className="rounded-lg bg-surface-secondary p-4">
      <h3 className="mb-3 flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-text-muted)]">
        UC Honors Cap
        <HelpTip content={HONORS_CAP_HELP} label="About UC honors cap" placement="bottom" />
      </h3>
      <div className="space-y-3">
        {/* Total Cap */}
        <div>
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="text-[var(--color-text-secondary)]">Total ({honorsCap.usedPoints} of {honorsCap.totalCap})</span>
            <span className={honorsCap.remainingPoints > 0 ? 'text-success' : 'text-error'}>
              <NumericDisplay value={honorsCap.remainingPoints} decimals={0} inline /> remaining
            </span>
          </div>
          <div className="h-3 overflow-hidden rounded-full bg-[var(--color-bg-tertiary)]">
            <div
              className={`h-3 rounded-full transition-all duration-500 ${barColor}`}
              style={{ width: `${Math.min(totalPct, 100)}%` }}
            />
          </div>
        </div>

        {/* 10th Grade Sub-Cap */}
        <div>
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="text-[var(--color-text-secondary)]">10th Grade ({honorsCap.tenthGradeUsed} of {honorsCap.tenthGradeCap})</span>
            <span className={honorsCap.tenthGradeRemaining > 0 ? 'text-success' : 'text-warning'}>
              <NumericDisplay value={honorsCap.tenthGradeRemaining} decimals={0} inline /> remaining
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-[var(--color-bg-tertiary)]">
            <div
              className="h-2 rounded-full bg-info transition-all duration-500"
              style={{ width: `${Math.min(tenthPct, 100)}%` }}
            />
          </div>
        </div>

        {/* Breakdown of courses using honors points */}
        {honorsCourses.length > 0 && (
          <div className="mt-2 space-y-1">
            <p className="text-xs font-medium text-[var(--color-text-secondary)]">Courses contributing honors points:</p>
            <ul className="space-y-0.5">
              {tenthGradeHonors.slice(0, 4).map((c) => (
                <li key={c.id} className="flex items-center gap-1.5 text-xs text-[var(--color-text)]">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-info" />
                  <span className="truncate">{c.name}</span>
                  <span className="shrink-0 text-[var(--color-text-muted)]">
                    ({COURSE_TYPE_LABELS[c.courseType] ?? c.courseType}) · 10th
                  </span>
                </li>
              ))}
              {eleventhGradeHonors.map((c) => (
                <li key={c.id} className="flex items-center gap-1.5 text-xs text-[var(--color-text)]">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-success" />
                  <span className="truncate">{c.name}</span>
                  <span className="shrink-0 text-[var(--color-text-muted)]">
                    ({COURSE_TYPE_LABELS[c.courseType] ?? c.courseType}) · 11th
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* FE-E2 — Honors Cap Saturation Advisory */}
        {showAdvisory && (
          <div
            className="mt-3 rounded-md border border-info bg-info-light p-2.5 text-xs"
            data-testid="honors-cap-advisory"
          >
            <p className="font-medium text-[var(--color-text)]">
              Honors cap reached &mdash; &minus;{capPenalty.toFixed(2)} from your UC Capped GPA
            </p>
            <p className="mt-0.5 text-[var(--color-text-secondary)]">
              You&rsquo;ve used all 8 UC honors points. Adding more honors courses raises your UC Uncapped &amp; Common App GPA, but not UC Capped.
              {schoolContext.limitedAP === true && (
                <span> (Your school&rsquo;s limited AP offerings make cap saturation expected.)</span>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
